import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lightbulb, ArrowRight, ArrowLeft, CheckCircle, Target, ListChecks, Scale, ClipboardList, Plus, Trash2 } from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const ProblemSolvingPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    problem: "",
    goal: "",
    solutions: ["", ""],
    pros_cons: [{}, {}],
    chosen_solution: "",
    action_steps: [""],
    reflection: "",
  });

  const STEPS = [
    { icon: Target, title: t("problem.step.problem"), desc: t("problem.step.problemDesc") },
    { icon: Lightbulb, title: t("problem.step.goals"), desc: t("problem.step.goalsDesc") },
    { icon: ListChecks, title: t("problem.step.solutions"), desc: t("problem.step.solutionsDesc") },
    { icon: Scale, title: t("problem.step.evaluate"), desc: t("problem.step.evaluateDesc") },
    { icon: ClipboardList, title: t("problem.step.plan"), desc: t("problem.step.planDesc") },
  ];

  const addSolution = () => {
    setFormData(prev => ({
      ...prev,
      solutions: [...prev.solutions, ""],
      pros_cons: [...prev.pros_cons, {}],
    }));
  };

  const removeSolution = (idx) => {
    if (formData.solutions.length <= 2) return;
    setFormData(prev => ({
      ...prev,
      solutions: prev.solutions.filter((_, i) => i !== idx),
      pros_cons: prev.pros_cons.filter((_, i) => i !== idx),
    }));
  };

  const addStep = () => {
    setFormData(prev => ({ ...prev, action_steps: [...prev.action_steps, ""] }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/problem-solving`, formData, { withCredentials: true });
      toast.success(t("problem.saved"));
      if (user?.role === "patient") promptShare(res.data, "problem_solving");
      setFormData({ problem: "", goal: "", solutions: ["", ""], pros_cons: [{}, {}], chosen_solution: "", action_steps: [""], reflection: "" });
      setStep(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <Label>{t("problem.whatProblem")}</Label>
            <Textarea placeholder={t("problem.problemPlaceholder")} value={formData.problem} onChange={(e) => setFormData({ ...formData, problem: e.target.value })} rows={4} data-testid="problem-input" />
            <p className="text-xs text-muted-foreground">{t("problem.problemTip")}</p>
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <Label>{t("problem.whatGoal")}</Label>
            <Textarea placeholder={t("problem.goalPlaceholder")} value={formData.goal} onChange={(e) => setFormData({ ...formData, goal: e.target.value })} rows={3} data-testid="goal-input" />
            <p className="text-xs text-muted-foreground">{t("problem.smartTip")}</p>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t("problem.generateSolutions")}</p>
            {formData.solutions.map((sol, idx) => (
              <div key={idx} className="flex gap-2 items-start">
                <div className="flex-1 space-y-1">
                  <Label>{t("problem.solution")} {idx + 1}</Label>
                  <Input placeholder={t("problem.solutionPlaceholder")} value={sol} onChange={(e) => {
                    const newSols = [...formData.solutions];
                    newSols[idx] = e.target.value;
                    setFormData({ ...formData, solutions: newSols });
                  }} data-testid={`solution-${idx}`} />
                </div>
                {formData.solutions.length > 2 && (
                  <Button variant="ghost" size="sm" className="mt-6" onClick={() => removeSolution(idx)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addSolution} data-testid="add-solution-btn">
              <Plus className="w-4 h-4 mr-1" /> {t("problem.addSolution")}
            </Button>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">{t("problem.evaluateIntro")}</p>
            {formData.solutions.filter(Boolean).map((sol, idx) => (
              <Card key={idx}>
                <CardContent className="pt-4 space-y-3">
                  <p className="font-medium">{sol}</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-green-600">{t("problem.pros")}</Label>
                      <Textarea placeholder={t("problem.prosPlaceholder")} value={formData.pros_cons[idx]?.pros || ""} onChange={(e) => {
                        const newPC = [...formData.pros_cons];
                        newPC[idx] = { ...newPC[idx], pros: e.target.value };
                        setFormData({ ...formData, pros_cons: newPC });
                      }} rows={2} />
                    </div>
                    <div>
                      <Label className="text-red-600">{t("problem.cons")}</Label>
                      <Textarea placeholder={t("problem.consPlaceholder")} value={formData.pros_cons[idx]?.cons || ""} onChange={(e) => {
                        const newPC = [...formData.pros_cons];
                        newPC[idx] = { ...newPC[idx], cons: e.target.value };
                        setFormData({ ...formData, pros_cons: newPC });
                      }} rows={2} />
                    </div>
                  </div>
                  <Button variant={formData.chosen_solution === sol ? "default" : "outline"} size="sm" onClick={() => setFormData({ ...formData, chosen_solution: sol })} data-testid={`choose-solution-${idx}`}>
                    {formData.chosen_solution === sol ? <CheckCircle className="w-4 h-4 mr-1" /> : null}
                    {t("problem.chosenSolution")}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            {formData.chosen_solution && (
              <div className="p-3 bg-primary/5 rounded-lg">
                <p className="text-sm font-medium">{t("problem.chosenSolution")}: {formData.chosen_solution}</p>
              </div>
            )}
            <Label>{t("problem.actionSteps")}</Label>
            {formData.action_steps.map((as, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <span className="text-sm text-muted-foreground w-6">{idx + 1}.</span>
                <Input placeholder={t("problem.stepPlaceholder")} value={as} onChange={(e) => {
                  const newSteps = [...formData.action_steps];
                  newSteps[idx] = e.target.value;
                  setFormData({ ...formData, action_steps: newSteps });
                }} data-testid={`action-step-${idx}`} />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addStep} data-testid="add-step-btn">
              <Plus className="w-4 h-4 mr-1" /> {t("problem.addStep")}
            </Button>
          </div>
        );
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="problem-solving-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("problem.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("problem.pageSubtitle")}</p>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.problem_solving} />

        {/* Step indicators */}
        <div className="flex gap-1.5 max-w-3xl mx-auto">
          {STEPS.map((s, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full cursor-pointer transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`} onClick={() => setStep(i)} />
          ))}
        </div>

        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              {(() => { const SIcon = STEPS[step].icon; return <SIcon className="w-5 h-5" />; })()}
              {STEPS[step].title}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{STEPS[step].desc}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderStepContent()}

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-1" />
              </Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={() => setStep(step + 1)}>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} className="bg-primary hover:bg-primary/90" data-testid="save-problem">
                  <CheckCircle className="w-4 h-4 mr-1" /> {submitting ? t("exercise.saving") : t("common.save")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default ProblemSolvingPage;
